import { useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { match } from "ts-pattern";

import { toFormikValidate } from "@thalamos/common";
import {
  displayName,
  mayActInPersonDirectory,
  PERSON_MATCH_LIMIT,
  type PersonId,
  type PersonMatches,
  type PersonMatchRequest,
} from "@thalamos/haven-common";

import api from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import { createRoute } from "../../routing/createRoute.js";
import {
  emptyMatchPersonValues,
  MatchPersonValuesSchema,
  readMatchHandOff,
  valuesFromRequest,
  type MatchPersonValues,
} from "./matchPerson.js";

/** What a search came back with, as the page needs it. */
export type SearchOutcome = PersonMatches | "failed";

/** Finds whether a young person is already known before one is registered. */
export const MatchPersonPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const sessionContext = user?.sessionContext ?? null;
  const handOff = readMatchHandOff(useLocation().state);

  const search = async (
    request: PersonMatchRequest,
  ): Promise<SearchOutcome> => {
    const response = await api.person.match(request);

    return response.status === 200 &&
      typeof response.data === "object" &&
      response.data !== null &&
      "searchOutcome" in response.data
      ? response.data
      : "failed";
  };

  return (
    <MatchPersonPageContent
      mayMatch={mayActInPersonDirectory(sessionContext, "match")}
      mayCreate={mayActInPersonDirectory(sessionContext, "create")}
      handOff={handOff}
      onSearch={search}
      onOpen={(personId) => {
        navigate(createRoute.person(personId));
      }}
      onCreate={(request) => {
        navigate(createRoute.newPerson(), { state: request });
      }}
    />
  );
};

const DATE_OF_BIRTH = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeZone: "UTC",
});

type Shown = "nothing" | "searching" | SearchOutcome;

/**
 * The page's rendering, with the search and where it leads injected so a story
 * or a test can show every state without an API client or a router.
 */
export const MatchPersonPageContent = ({
  mayMatch,
  mayCreate,
  handOff,
  onSearch,
  onOpen,
  onCreate,
}: {
  mayMatch: boolean;
  mayCreate: boolean;
  /** A search that ran before, to run again on arrival. */
  handOff: PersonMatchRequest | null;
  onSearch: (request: PersonMatchRequest) => Promise<SearchOutcome>;
  onOpen: (personId: PersonId) => void;
  onCreate: (request: PersonMatchRequest) => void;
}) => {
  const { t } = useTranslation();
  const [shown, setShown] = useState<Shown>("nothing");
  const [ran, setRan] = useState<PersonMatchRequest | undefined>(undefined);
  const [chosen, setChosen] = useState<PersonId | undefined>(undefined);

  // Counts searches started or forgotten, so an answer that arrives after the
  // search it answers was replaced or cleared is dropped.
  const latest = useRef(0);

  const run = async (request: PersonMatchRequest) => {
    const searchNumber = ++latest.current;
    setShown("searching");
    setRan(request);
    setChosen(undefined);
    const outcome = await onSearch(request).catch(
      (): SearchOutcome => "failed",
    );
    if (searchNumber === latest.current) {
      setShown(outcome);
    }
  };

  const forget = () => {
    latest.current++;
    setShown("nothing");
    setRan(undefined);
    setChosen(undefined);
  };

  const initialValues =
    handOff === null ? emptyMatchPersonValues() : valuesFromRequest(handOff);

  // Once, on arrival, even when the effect runs twice: the hand-off is what the
  // page was opened with, so later renders must not run it again.
  const ranOnArrival = useRef(false);
  useEffect(() => {
    if (ranOnArrival.current || handOff === null) {
      return;
    }

    ranOnArrival.current = true;
    const request = MatchPersonValuesSchema.safeParse(initialValues);
    if (request.success) {
      void run(request.data);
    }
  }, []);

  if (!mayMatch) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 760 }}>
        <Typography variant="h4" component="h1">
          {t("personMatch.title")}
        </Typography>
        <Alert severity="info">{t("personMatch.notPermitted")}</Alert>
      </Stack>
    );
  }

  const matched =
    typeof shown === "object" && shown.searchOutcome === "success"
      ? shown.persons
      : [];
  const foundNobody =
    typeof shown === "object" &&
    shown.searchOutcome === "success" &&
    shown.persons.length === 0;
  // Registering follows a search that answered, however many it found; one that
  // failed has checked for nobody.
  const canCreate = mayCreate && ran !== undefined && typeof shown === "object";

  return (
    <Formik<MatchPersonValues>
      initialValues={initialValues}
      validate={toFormikValidate(MatchPersonValuesSchema)}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values) => {
        await run(MatchPersonValuesSchema.parse(values));
      }}
    >
      {({ values, errors, handleChange, setFieldValue, resetForm }) => {
        const errorFor = (field: keyof MatchPersonValues) =>
          errors[field] === undefined ? undefined : t(errors[field]);

        // A result chosen from one search is not a choice from the next, and
        // keeping it would hide Search behind Continue.
        const edit = (
          event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          handleChange(event);
          setChosen(undefined);
        };

        return (
          <Form noValidate>
            <Stack spacing={3} sx={{ maxWidth: 760 }}>
              <Typography variant="h4" component="h1">
                {t("personMatch.title")}
              </Typography>

              <FormControl>
                <FormLabel id="search-by">
                  {t("personMatch.searchBy")}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="search-by"
                  name="searchBy"
                  value={values.searchBy}
                  onChange={(event) => {
                    void setFieldValue("searchBy", event.target.value);
                    forget();
                  }}
                >
                  <FormControlLabel
                    value="upn"
                    control={<Radio />}
                    label={t("personMatch.byUpn")}
                  />
                  <FormControlLabel
                    value="demographics"
                    control={<Radio />}
                    label={t("personMatch.byDemographics")}
                  />
                </RadioGroup>
              </FormControl>

              {values.searchBy === "upn" ? (
                <TextField
                  id="search-upn"
                  name="upn"
                  label={t("personMatch.upn")}
                  value={values.upn}
                  onChange={edit}
                  error={errorFor("upn") !== undefined}
                  helperText={errorFor("upn")}
                  sx={{ maxWidth: 320 }}
                />
              ) : (
                <>
                  <Stack spacing={0.5}>
                    <TextField
                      id="search-given"
                      name="given"
                      label={t("personMatch.given")}
                      value={values.given}
                      onChange={edit}
                      error={errorFor("given") !== undefined}
                      helperText={errorFor("given")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="phoneticGiven"
                          checked={values.phoneticGiven}
                          onChange={edit}
                        />
                      }
                      label={t("personMatch.similarSoundingGiven")}
                    />
                  </Stack>

                  <Stack spacing={0.5}>
                    <TextField
                      id="search-family"
                      name="family"
                      label={t("personMatch.family.label")}
                      value={values.family}
                      onChange={edit}
                      error={errorFor("family") !== undefined}
                      helperText={errorFor("family")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="phoneticFamily"
                          checked={values.phoneticFamily}
                          onChange={edit}
                        />
                      }
                      label={t("personMatch.similarSoundingFamily")}
                    />
                  </Stack>

                  <FormControl
                    error={errorFor("dateOfBirthKnown") !== undefined}
                  >
                    <FormLabel id="date-of-birth-known">
                      {t("personMatch.dateOfBirthKnown.label")}
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="date-of-birth-known"
                      name="dateOfBirthKnown"
                      value={values.dateOfBirthKnown}
                      onChange={edit}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label={t("personMatch.yes")}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label={t("personMatch.no")}
                      />
                    </RadioGroup>
                    {errorFor("dateOfBirthKnown") === undefined ? null : (
                      <FormHelperText>
                        {errorFor("dateOfBirthKnown")}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {values.dateOfBirthKnown === "yes" ? (
                    <TextField
                      id="search-date-of-birth"
                      name="dateOfBirth"
                      type="date"
                      label={t("personMatch.dateOfBirth.label")}
                      value={values.dateOfBirth}
                      onChange={edit}
                      error={errorFor("dateOfBirth") !== undefined}
                      helperText={errorFor("dateOfBirth")}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ maxWidth: 280 }}
                    />
                  ) : null}
                </>
              )}

              {match(shown)
                .with("nothing", () => (
                  <Alert severity="warning">
                    {t("personMatch.searchFirst")}
                  </Alert>
                ))
                .with("searching", () => null)
                .with("failed", () => (
                  <Alert severity="error">{t("personMatch.failed")}</Alert>
                ))
                .with({ searchOutcome: "too-many-matches" }, () => (
                  <Alert severity="warning">
                    <AlertTitle>{t("personMatch.tooMany.title")}</AlertTitle>
                    {t("personMatch.tooMany.body", {
                      limit: PERSON_MATCH_LIMIT,
                    })}
                  </Alert>
                ))
                .with({ searchOutcome: "success" }, ({ persons }) =>
                  persons.length === 0 ? (
                    <Alert severity="error">
                      <AlertTitle>{t("personMatch.none.title")}</AlertTitle>
                      {t("personMatch.none.body")}
                    </Alert>
                  ) : (
                    <Alert severity="success">
                      <AlertTitle>{t("personMatch.found.title")}</AlertTitle>
                      {t("personMatch.found.body")}
                    </Alert>
                  ),
                )
                .exhaustive()}

              {matched.length > 0 ? (
                <Stack spacing={1}>
                  <Typography variant="h6" component="h2">
                    {t("personMatch.results")}
                  </Typography>
                  <RadioGroup
                    aria-label={t("personMatch.results")}
                    value={chosen ?? ""}
                    onChange={(event) => {
                      setChosen(
                        matched.find(
                          (person) => person.id === event.target.value,
                        )?.id,
                      );
                    }}
                  >
                    <Stack spacing={1}>
                      {matched.map((person) => (
                        <Paper
                          key={person.id}
                          variant="outlined"
                          sx={{ px: 2, py: 1 }}
                        >
                          <FormControlLabel
                            value={person.id}
                            control={<Radio />}
                            label={
                              <Stack direction="row" spacing={5}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t("personListing.name")}
                                  </Typography>
                                  <Typography>
                                    {displayName(person.names)}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t("personListing.dateOfBirth")}
                                  </Typography>
                                  <Typography>
                                    {person.dateOfBirth === null
                                      ? t("personListing.notRecorded")
                                      : DATE_OF_BIRTH.format(
                                          new Date(
                                            `${person.dateOfBirth}T00:00:00Z`,
                                          ),
                                        )}
                                  </Typography>
                                </Box>
                              </Stack>
                            }
                          />
                        </Paper>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Stack>
              ) : null}

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    resetForm({ values: emptyMatchPersonValues() });
                    forget();
                  }}
                >
                  {t("personMatch.clear")}
                </Button>
                {mayCreate ? (
                  <Button
                    variant={foundNobody ? "contained" : "outlined"}
                    disabled={!canCreate}
                    onClick={() => {
                      if (ran !== undefined) {
                        onCreate(ran);
                      }
                    }}
                  >
                    {t("personMatch.create")}
                  </Button>
                ) : null}
                <Box sx={{ flexGrow: 1 }} />
                {chosen === undefined ? (
                  <Button
                    type="submit"
                    variant={foundNobody ? "outlined" : "contained"}
                    disabled={shown === "searching"}
                  >
                    {t("personMatch.search")}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => {
                      onOpen(chosen);
                    }}
                  >
                    {t("personMatch.continue")}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};
